// tasteBalanceCritic — deterministic post-generation balance check
// (roadmap A4). Tallies acid / fat / salt / sweet / heat / umami
// sources across a finished recipe's BOUND ingredients using the
// enrichment taste scales (info.flavor_profile, 0-5) with a curated
// keyword fallback for canonicals not yet enriched. On a clear
// imbalance it returns ONE user-visible "chef's balance note"
// suggesting ONE finishing improvement.
//
// Contract (dream-mode safe by construction):
//   - NEVER mutates the recipe. NEVER blocks. String or null.
//   - At most one note — this is a tasting remark, not a report card.
//   - Zero model calls; pure derivation, so it can't hallucinate.

// Fallback nets for the (still large) unenriched tail. Substring
// match on canonical ids — deliberately coarse; the scales win when
// present.
const ACID_HINTS = ["lemon", "lime", "vinegar", "tamarind", "tomatillo", "tomato", "yogurt", "buttermilk", "pickle", "kimchi", "sauerkraut", "sumac", "wine", "orange", "grapefruit", "pomegranate", "verjuice", "sour"];
const FAT_HINTS = ["butter", "oil", "cream", "cheese", "bacon", "lard", "tahini", "mayo", "coconut_milk", "coconut_cream", "sausage", "chorizo", "pancetta", "guanciale", "duck_fat", "schmaltz", "avocado", "nut_butter", "peanut_butter"];
const SWEET_HINTS = ["sugar", "honey", "maple", "molasses", "agave", "jam", "date", "caramel", "sweetened_condensed", "mirin", "hoisin"];
const SALT_HINTS = ["salt", "soy_sauce", "fish_sauce", "miso", "anchovy", "caper", "olive", "parmesan", "pecorino", "feta", "prosciutto", "worcestershire", "oyster_sauce", "doenjang", "gochujang"];
const HEAT_HINTS = ["chile", "chili", "pepper_flake", "cayenne", "jalapeno", "serrano", "habanero", "gochugaru", "harissa", "sriracha", "hot_sauce", "wasabi", "horseradish", "black_pepper", "sichuan_pepper", "aleppo", "chipotle", "sambal", "calabrian"];

function idHits(id, hints) {
  const s = String(id || "").toLowerCase();
  return !!s && hints.some((h) => s.includes(h));
}

function scaleOf(info, key) {
  const v = info?.flavor_profile?.[key];
  return Number.isFinite(v) ? v : null;
}

/**
 * @param {object} recipe        — finished recipe (ingredients[].ingredientId)
 * @param {(id) => object|null} getInfo — merged enrichment resolver
 * @returns {string|null}        — one balance note, or null when balanced/unknowable
 */
export function tasteBalanceNote(recipe, getInfo) {
  const ings = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  if (ings.length < 3) return null;

  let acid = 0, fat = 0, sweet = 0, salt = 0, heat = 0, richFat = 0;
  for (const ing of ings) {
    const id = ing?.ingredientId;
    if (!id) continue;
    const info = typeof getInfo === "function" ? getInfo(id) : null;
    const sSour = scaleOf(info, "sour");
    const sFat = scaleOf(info, "fat");
    const sSweet = scaleOf(info, "sweet");
    const sSalt = scaleOf(info, "salty");
    const sHeat = scaleOf(info, "heat");
    const hasScales = [sSour, sFat, sSweet, sSalt, sHeat].some((v) => v !== null);

    if ((sSour !== null && sSour >= 3) || (!hasScales && idHits(id, ACID_HINTS))) acid += 1;
    if ((sFat !== null && sFat >= 3) || (!hasScales && idHits(id, FAT_HINTS))) fat += 1;
    if (sFat !== null && sFat >= 4) richFat += 1;
    if ((sSweet !== null && sSweet >= 3) || (!hasScales && idHits(id, SWEET_HINTS))) sweet += 1;
    if ((sSalt !== null && sSalt >= 3) || (!hasScales && idHits(id, SALT_HINTS))) salt += 1;
    if ((sHeat !== null && sHeat >= 2) || (!hasScales && idHits(id, HEAT_HINTS))) heat += 1;
  }

  // Desserts / bakes play by pastry rules — the savory balance axes
  // below would false-positive on every cookie. Skip them.
  const course = String(recipe?.course || "").toLowerCase();
  const isSweetCourse = course === "dessert" || course === "bake"
    || /cookie|cake|brownie|pie|tart|pastry|bagel|bread|muffin/i.test(recipe?.title || "");

  if (!isSweetCourse && (fat >= 2 || richFat >= 1) && acid === 0) {
    return "Rich and fat-forward with no acid anywhere in the build — a squeeze of citrus, a splash of vinegar, or something quick-pickled at the end would cut it and wake the whole plate up.";
  }
  if (!isSweetCourse && sweet >= 2 && salt === 0 && heat === 0) {
    return "Sweetness is running unopposed — a proper pinch of salt at the finish, or a little heat, would keep it from reading flat.";
  }
  return null;
}
